import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

// Last-line-of-defense for runtime errors (render errors, bad props, unhandled
// renderer state). Logs to console; in production a dedicated error reporter
// (Sentry etc.) can hook into componentDidCatch. Keeps the user from seeing
// a blank white page — especially painful on mobile where there's no inspector.
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  private reset = () => {
    this.setState({ error: null });
  };

  private reload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="min-h-dvh grid place-items-center p-4 bg-surface-alt">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-hair p-6 space-y-4">
          <div className="mx-auto h-10 w-10 rounded-full bg-red-50 grid place-items-center text-red-600">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div className="text-center space-y-1">
            <h1 className="font-bold">Une erreur est survenue</h1>
            <p className="text-body text-ink-mute">
              {this.state.error.message || 'Erreur inconnue'}
            </p>
          </div>
          <div className="flex gap-2 justify-center">
            <button
              type="button"
              onClick={this.reset}
              className="px-4 py-2 rounded-lg border border-hair text-body hover:bg-surface-alt"
            >
              Réessayer
            </button>
            <button
              type="button"
              onClick={this.reload}
              className="px-4 py-2 rounded-lg bg-ink text-white text-body hover:bg-ink/90 inline-flex items-center gap-1.5"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Recharger
            </button>
          </div>
        </div>
      </div>
    );
  }
}
